import { useEffect } from 'react';
import { Form, Select, InputNumber, DatePicker, Input, Button } from 'antd';
import dayjs from 'dayjs';
import {
  FluxoCaixaCentroCusto,
  FluxoCaixaFormData,
  FluxoCaixaTipo,
  FLUXO_CAIXA_CENTRO_CUSTO_LABELS,
  FLUXO_CAIXA_TIPO_LABELS,
  getCategoriasPorTipo,
  FLUXO_CAIXA_CATEGORIA_LABELS,
} from '../types/fluxoCaixa';

interface FluxoCaixaFormProps {
  onSubmit: (data: FluxoCaixaFormData) => Promise<void>;
  loading?: boolean;
  formKey?: number;
  onCancel?: () => void;
  submitLabel?: string;
  variant?: 'inline' | 'modal';
}

export function FluxoCaixaForm({
  onSubmit,
  loading = false,
  formKey = 0,
  onCancel,
  submitLabel = 'Lançar',
  variant = 'inline',
}: FluxoCaixaFormProps) {
  const [form] = Form.useForm();
  const tipoSelecionado = Form.useWatch('lancamento_tipo', form) as FluxoCaixaTipo | undefined;

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({
      lancamento_tipo: FluxoCaixaTipo.ENTRADA,
      data_lancamento: dayjs(),
    });
  }, [formKey, form]);

  const handleTipoChange = (tipo: FluxoCaixaTipo) => {
    const categorias = getCategoriasPorTipo(tipo);
    form.setFieldValue('categoria', categorias[0]);
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    const formData: FluxoCaixaFormData = {
      lancamento_tipo: values.lancamento_tipo as FluxoCaixaTipo,
      categoria: values.categoria as FluxoCaixaFormData['categoria'],
      centro_custo: values.centro_custo as FluxoCaixaCentroCusto | undefined,
      valor: values.valor as number,
      descricao: values.descricao as string | undefined,
      documento: values.documento as string | undefined,
      data_lancamento: (values.data_lancamento as dayjs.Dayjs).format('YYYY-MM-DD'),
    };

    await onSubmit(formData);

    if (variant === 'inline') {
      form.resetFields();
      form.setFieldsValue({
        lancamento_tipo: FluxoCaixaTipo.ENTRADA,
        data_lancamento: dayjs(),
      });
    }
  };

  const categorias = tipoSelecionado
    ? getCategoriasPorTipo(tipoSelecionado)
    : getCategoriasPorTipo(FluxoCaixaTipo.ENTRADA);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      autoComplete="off"
      initialValues={{
        lancamento_tipo: FluxoCaixaTipo.ENTRADA,
        data_lancamento: dayjs(),
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <Form.Item
          name="lancamento_tipo"
          label="Tipo"
          rules={[{ required: true, message: 'Selecione o tipo' }]}
        >
          <Select onChange={handleTipoChange}>
            {Object.values(FluxoCaixaTipo).map(tipo => (
              <Select.Option key={tipo} value={tipo}>
                {FLUXO_CAIXA_TIPO_LABELS[tipo]}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="categoria"
          label="Categoria"
          rules={[{ required: true, message: 'Selecione a categoria' }]}
        >
          <Select placeholder="Selecione">
            {categorias.map(cat => (
              <Select.Option key={cat} value={cat}>
                {FLUXO_CAIXA_CATEGORIA_LABELS[cat]}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      <Form.Item
        name="valor"
        label="Valor (R$)"
        rules={[
          { required: true, message: 'Informe o valor' },
          { type: 'number', min: 0.01, message: 'Valor deve ser maior que zero' },
        ]}
      >
        <InputNumber
          placeholder="0,00"
          min={0.01}
          step={0.01}
          precision={2}
          formatter={value => `R$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          style={{ width: '100%' }}
        />
      </Form.Item>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <Form.Item
          name="data_lancamento"
          label="Data"
          rules={[{ required: true, message: 'Informe a data' }]}
        >
          <DatePicker
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            disabledDate={current => current && current > dayjs().endOf('day')}
          />
        </Form.Item>

        <Form.Item name="centro_custo" label="Centro de Custo">
          <Select allowClear placeholder="Opcional">
            {Object.values(FluxoCaixaCentroCusto).map(cc => (
              <Select.Option key={cc} value={cc}>
                {FLUXO_CAIXA_CENTRO_CUSTO_LABELS[cc]}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      <Form.Item name="documento" label="Documento / Referência">
        <Input maxLength={100} placeholder="NF, comprovante..." />
      </Form.Item>

      <Form.Item name="descricao" label="Descrição">
        <Input.TextArea maxLength={500} rows={3} placeholder="Descrição opcional" showCount />
      </Form.Item>

      {variant === 'modal' && onCancel ? (
        <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-zinc-700">
          <Button onClick={onCancel} style={{ flex: 1 }} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
            style={{ flex: 1, backgroundColor: '#FF3737' }}
          >
            {submitLabel}
          </Button>
        </div>
      ) : (
        <div className="flex justify-end pt-2 border-t border-gray-100 dark:border-zinc-800 mt-2">
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ backgroundColor: '#FF3737' }}
          >
            {submitLabel}
          </Button>
        </div>
      )}
    </Form>
  );
}
